import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, BarChart3, Users, Target, TrendingUp, Sparkles, Send, Globe2, Layers,
} from "lucide-react";import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, Legend,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = [
  "#0891b2", "#6366f1", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#84cc16", "#f97316", "#ec4899",
];

const STATUS_COLORS = {
  New: "#94a3b8",
  Reviewed: "#64748b",
  Interested: "#10b981",
  DemoSent: "#06b6d4",
  FollowUpNeeded: "#f59e0b",
  PilotCandidate: "#6366f1",
  Converted: "#059669",
  NotInterested: "#ef4444",
};

export default function AdminCrmReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/crm/reports`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load reports");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading reports…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
        {error || "No data"}
      </div>
    );
  }

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="crm-reports-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM Reports</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Pipeline funnel, conversion, and outreach health.
        </p>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Contacts" value={t.contacts ?? 0} icon={Users} color="cyan" />
        <Stat label="Converted" value={t.converted ?? 0} icon={TrendingUp} color="emerald" />
        <Stat label="Pilot candidates" value={t.pilots ?? 0} icon={Target} color="indigo" />
        <Stat label="Conversion rate" value={`${t.conversion_rate ?? 0}%`} icon={BarChart3} color="amber" />
        <Stat label="Outreach (30d)" value={t.outreach_30d ?? 0} icon={Send} color="cyan" />
        <Stat label="AI drafted (30d)" value={t.ai_generated_30d ?? 0} icon={Sparkles} color="indigo" />
      </div>

      {/* Outreach email metrics (Round 5) */}
      {data.outreach_metrics && (
        <Section title="AI outreach email funnel (last 30 days)" icon={Send} testid="crm-reports-outreach-metrics">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <MetricChip label="Drafted" value={data.outreach_metrics.drafted} tone="indigo" />
            <MetricChip label="Sent" value={data.outreach_metrics.sent} tone="cyan" />
            <MetricChip label="Delivered" value={data.outreach_metrics.delivered} tone="emerald" sub={`${data.outreach_metrics.delivery_rate}%`} />
            <MetricChip label="Opened" value={data.outreach_metrics.opened} tone="emerald" sub={`${data.outreach_metrics.open_rate}%`} />
            <MetricChip label="Clicked" value={data.outreach_metrics.clicked} tone="amber" sub={`${data.outreach_metrics.click_rate}%`} />
            <MetricChip label="Replied" value={data.outreach_metrics.replied} tone="indigo" />
            <MetricChip label="Bounced" value={data.outreach_metrics.bounced} tone="rose" />
            <MetricChip label="Failed" value={data.outreach_metrics.failed} tone="rose" />
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Sourced from <code className="font-mono">email_logs</code> with <code className="font-mono">submission_type=crm_outreach</code>. Statuses are updated live by the Resend webhook. Drafted = AI generations from the Outreach modal.
          </div>
        </Section>
      )}

      {/* Status funnel */}
      <Section title="Pipeline funnel — by status" icon={Layers} testid="crm-reports-status-funnel">
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.status_funnel} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.status_funnel.map((entry) => (
                  <Cell key={entry.stage} fill={STATUS_COLORS[entry.stage] || "#0891b2"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Outreach stage funnel */}
      <Section title="Outreach stage funnel" icon={Send} testid="crm-reports-stage-funnel">
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.outreach_stage_funnel} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={80} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Category */}
        <Section title="By category" icon={Users} testid="crm-reports-by-category">
          {data.by_category?.length ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.by_category}
                    dataKey="count"
                    nameKey="key"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {data.by_category.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Empty />
          )}
        </Section>

        {/* By Country */}
        <Section title="By country" icon={Globe2} testid="crm-reports-by-country">
          {data.by_country?.length ? (
            <ProportionalList items={data.by_country} accent="#0891b2" />
          ) : <Empty />}
        </Section>
      </div>

      <Section title="By source" icon={Layers} testid="crm-reports-by-source">
        {data.by_source?.length ? (
          <ProportionalList items={data.by_source} accent="#6366f1" />
        ) : <Empty />}
      </Section>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color = "cyan" }) {
  const tones = {
    cyan: "bg-cyan-50 ring-cyan-200 text-cyan-700",
    indigo: "bg-indigo-50 ring-indigo-200 text-indigo-700",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-700",
    amber: "bg-amber-50 ring-amber-200 text-amber-700",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`crm-reports-stat-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ring-1 ${tones[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-slate-500" />
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ProportionalList({ items, accent }) {
  const max = Math.max(...items.map((x) => x.count));
  return (
    <ul className="space-y-2">
      {items.map((c) => (
        <li key={c.key} className="text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-700 truncate">{c.key}</span>
            <span className="text-xs font-semibold text-slate-500 tabular-nums">{c.count}</span>
          </div>
          <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${max ? (c.count / max) * 100 : 0}%`, backgroundColor: accent }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Empty() {
  return <div className="text-xs text-slate-400 text-center py-6">No data yet.</div>;
}

function MetricChip({ label, value, tone = "cyan", sub }) {
  const tones = {
    cyan: "bg-cyan-50 ring-cyan-200 text-cyan-800",
    indigo: "bg-indigo-50 ring-indigo-200 text-indigo-800",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-800",
    amber: "bg-amber-50 ring-amber-200 text-amber-800",
    rose: "bg-rose-50 ring-rose-200 text-rose-800",
  };
  return (
    <div
      className={`rounded-lg ring-1 px-3 py-2 ${tones[tone]}`}
      data-testid={`crm-reports-metric-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      {sub !== undefined && (
        <div className="text-[10px] opacity-70 tabular-nums">{sub}</div>
      )}
    </div>
  );
}
